import React, { useCallback, useMemo } from 'react';
import { getAccOrigin } from '@adsk/acs-sc-web-platform-api';
import * as analytics from '@adsk/acs-web-analytics';
import { safeSearchRegex } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import { factoryProjectEntitlementsKey } from './factoryProjectEntitlementsKey';
import useSearch from './useSearch';
import { ANALYTICS_TYPES } from '../helpers/analytics';
import { PROJECT_ENTITLEMENTS_CLASSIFICATION, PROJECT_ENTITLEMENTS_FIELDS, PROJECT_ENTITLEMENTS_LIMIT, TABS_NAMES, TEMPLATE_ENTITLEMENTS_CLASSIFICATION, TEMPLATE_ENTITLEMENTS_FIELDS, CLIENTS_PATH } from '../layout/consts';
import ProjectListAccountItem from '../layout/ProjectListAccountItem';
import ProjectListProjectItem from '../layout/ProjectListProjectItem';
import ProjectListTemplateItem from '../layout/ProjectListTemplateItem';
import ProjectRowLoader from '../layout/ProjectRowLoader';
import TemplateRowLoader from '../layout/TemplateRowLoader';
export const useProjectPickerTabs = function (_ref) {
  let {
    search,
    currentTab,
    accounts,
    hasAccounts,
    onClose
  } = _ref;
  const {
    T
  } = useLocale(_ref2 => {
    let {
      locale
    } = _ref2;
    return require("../i18n/".concat(locale, ".json"));
  });
  const accountsSearch = useMemo(() => accounts && accounts.filter(a => safeSearchRegex(search).test(a.account_display_name)) || [], [accounts, search]);
  const projectGetKeyMemo = useMemo(() => factoryProjectEntitlementsKey({
    path: CLIENTS_PATH,
    fields: PROJECT_ENTITLEMENTS_FIELDS,
    classification: PROJECT_ENTITLEMENTS_CLASSIFICATION,
    limit: PROJECT_ENTITLEMENTS_LIMIT,
    filterByProjectNumber: true
  }), []);
  const {
    loading: projectsLoading,
    error: projectsError,
    data: projects,
    totalResults: projectsTotalResults,
    retry: projectsRetry,
    hasNextPage: hasNextProjectsPage,
    nextPage: nextProjectsPage
  } = useSearch({
    enabled: currentTab === TABS_NAMES.PROJECTS,
    getKey: projectGetKeyMemo,
    search
  });
  const templateGetKeyMemo = useMemo(() => factoryProjectEntitlementsKey({
    path: CLIENTS_PATH,
    fields: TEMPLATE_ENTITLEMENTS_FIELDS,
    classification: TEMPLATE_ENTITLEMENTS_CLASSIFICATION,
    limit: PROJECT_ENTITLEMENTS_LIMIT
  }), []);
  const {
    loading: templatesLoading,
    error: templatesError,
    data: templates,
    totalResults: templatesTotalResults,
    retry: templatesRetry,
    hasNextPage: hasNextTemplatePage,
    nextPage: nextTemplatePage
  } = useSearch({
    enabled: currentTab === TABS_NAMES.TEMPLATES,
    getKey: templateGetKeyMemo,
    search
  });
  const handleAnalyticOnClick = useCallback(project => {
    if (project) {
      const analyticsType = project.classification === TABS_NAMES.TEMPLATES ? ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_TEMPLATE_CLICK : ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_PROJECT_CLICK;
      const data = {
        product_ids: project.products && project.products.map(product => product.key).join(', '),
        product_names: project.products && project.products.map(product => product.name).join(', '),
        project_id: project.id,
        project_name: project.name
      };
      analytics.track(analyticsType, data);
    }
  }, []);
  return useMemo(() => {
    var _T$translate, _T$translate2, _T$translate3;
    return [...(hasAccounts ? [{
      tab: TABS_NAMES.ACCOUNTS,
      label: (_T$translate = T.translate('ACCOUNTS')) === null || _T$translate === void 0 ? void 0 : _T$translate.toString(),
      loading: false,
      error: false,
      hasNextPage: false,
      totalResults: accountsSearch.length,
      data: accountsSearch,
      retry: () => null,
      renderLoader: () => /*#__PURE__*/React.createElement(ProjectRowLoader, null),
      renderItem: _ref3 => {
        let {
          item,
          search,
          currentAccountId,
          currentProductId,
          onAccountClick,
          containerRef
        } = _ref3;
        return /*#__PURE__*/React.createElement(ProjectListAccountItem, {
          key: item === null || item === void 0 ? void 0 : item.account_id,
          item: item,
          search: search,
          currentAccountId: currentAccountId,
          currentProductId: currentProductId,
          onClose: onClose,
          onAccountClick: onAccountClick,
          containerRef: containerRef
        });
      }
    }] : []), {
      tab: TABS_NAMES.PROJECTS,
      label: (_T$translate2 = T.translate('PROJECTS')) === null || _T$translate2 === void 0 ? void 0 : _T$translate2.toString(),
      viewAllLabel: T.translate('PROJECT_PICKER_VIEW_ALL'),
      viewAllLink: "".concat(getAccOrigin(), "/projects"),
      loading: projectsLoading,
      error: projectsError,
      retry: projectsRetry,
      totalResults: projectsTotalResults,
      data: projects,
      fetchNextPage: nextProjectsPage,
      hasNextPage: hasNextProjectsPage,
      renderLoader: () => /*#__PURE__*/React.createElement(ProjectRowLoader, null),
      renderItem: _ref4 => {
        let {
          item,
          currentProjectId,
          currentProductId,
          onProjectClick,
          containerRef
        } = _ref4;
        return /*#__PURE__*/React.createElement(ProjectListProjectItem, {
          key: item === null || item === void 0 ? void 0 : item.id,
          item: item,
          search: search,
          currentProjectId: currentProjectId,
          currentProductId: currentProductId,
          onClose: onClose,
          onProjectClick: onProjectClick,
          handleAnalyticOnClick: handleAnalyticOnClick,
          containerRef: containerRef
        });
      }
    }, {
      tab: TABS_NAMES.TEMPLATES,
      label: (_T$translate3 = T.translate('TEMPLATES')) === null || _T$translate3 === void 0 ? void 0 : _T$translate3.toString(),
      viewAllLabel: T.translate('PROJECT_PICKER_VIEW_ALL'),
      viewAllLink: "".concat(getAccOrigin(), "/projects/templates"),
      loading: templatesLoading,
      error: templatesError,
      retry: templatesRetry,
      totalResults: templatesTotalResults,
      data: templates,
      fetchNextPage: nextTemplatePage,
      hasNextPage: hasNextTemplatePage,
      renderLoader: () => /*#__PURE__*/React.createElement(TemplateRowLoader, null),
      renderItem: _ref5 => {
        let {
          item,
          search,
          currentProjectId,
          currentProductId,
          onProjectClick,
          containerRef
        } = _ref5;
        return /*#__PURE__*/React.createElement(ProjectListTemplateItem, {
          key: item === null || item === void 0 ? void 0 : item.id,
          item: item,
          search: search,
          currentProjectId: currentProjectId,
          currentProductId: currentProductId,
          onClose: onClose,
          onProjectClick: onProjectClick,
          handleAnalyticOnClick: handleAnalyticOnClick,
          containerRef: containerRef
        });
      }
    }];
  }, [T, search, accountsSearch, handleAnalyticOnClick, hasAccounts, hasNextProjectsPage, hasNextTemplatePage, nextProjectsPage, nextTemplatePage, onClose, projects, projectsError, projectsLoading, projectsRetry, projectsTotalResults, templates, templatesError, templatesLoading, templatesRetry, templatesTotalResults]);
};
//# sourceMappingURL=useProjectPickerTabs.js.map